import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "@/admin/api";
import PageHeader, { Stat } from "@/admin/components/PageHeader";
import { ArrowRight } from "@phosphor-icons/react";

export default function Overview() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    adminApi.get("/overview").then((r) => setData(r.data)).catch((e) => setErr(e?.message));
  }, []);

  if (err) return <div className="p-8 text-red-600">Error: {err}</div>;
  if (!data) return <div className="p-8 text-zinc-500 font-mono text-xs">Loading…</div>;

  const t = data.totals;

  return (
    <div data-testid="admin-overview-page">
      <PageHeader eyebrow="Dashboard" title="Overview" description="Snapshot of inbound leads, campaigns and prospect database." />
      <div className="p-8 space-y-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Contact forms" value={t.contact_submissions} testId="stat-contacts" />
          <Stat label="Free trials" value={t.free_trial_submissions} testId="stat-trials" />
          <Stat label="Leads" value={t.leads} testId="stat-leads" />
          <Stat label="Campaigns" value={t.campaigns} testId="stat-campaigns" />
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <Panel title="Leads by source" empty={!data.leads_by_source.length}>
            <BarList items={data.leads_by_source.map((r) => ({ label: r.source, value: r.count }))} />
          </Panel>
          <Panel title="Leads by state (top 10)" empty={!data.leads_by_state.length}>
            <BarList items={data.leads_by_state.map((r) => ({ label: r.state, value: r.count }))} />
          </Panel>
          <Panel title="Campaigns by status" empty={!data.campaigns_by_status.length}>
            <BarList items={data.campaigns_by_status.map((r) => ({ label: r.status || "?", value: r.count }))} />
          </Panel>
          <Panel title="Recent contact submissions" empty={!data.recent_contacts.length}>
            <ul className="divide-y divide-zinc-200">
              {data.recent_contacts.map((c) => (
                <li key={c.id} className="py-3 text-sm flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-zinc-900 truncate">{c.name}</div>
                    <div className="text-xs text-zinc-500 truncate">{c.email}</div>
                  </div>
                  <div className="text-[10px] font-mono text-zinc-500">
                    {new Date(c.created_at).toLocaleDateString()}
                  </div>
                </li>
              ))}
            </ul>
            <Link to="/admin/contacts" className="mt-4 inline-flex items-center gap-2 text-sm text-[#0044FF] hover:underline">
              View all <ArrowRight size={12} weight="bold" />
            </Link>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function Panel({ title, children, empty }) {
  return (
    <div className="bg-white border border-zinc-200 p-6">
      <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-4">{title}</div>
      {empty ? (
        <div className="text-sm text-zinc-400">No data yet.</div>
      ) : (
        children
      )}
    </div>
  );
}

function BarList({ items }) {
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <ul className="space-y-2">
      {items.map((i) => (
        <li key={i.label} className="flex items-center gap-3">
          <span className="w-24 text-xs font-mono uppercase text-zinc-600 truncate">{i.label}</span>
          <div className="flex-1 h-6 bg-zinc-100 relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-[#0044FF]" style={{ width: `${(i.value / max) * 100}%` }} />
          </div>
          <span className="w-10 text-right text-xs font-mono text-zinc-700">{i.value}</span>
        </li>
      ))}
    </ul>
  );
}
